// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Autocomplete, Box, Card, Group, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { getReferenceString } from '@medplum/core';
import type { Condition, Encounter, Patient } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum } from '@medplum/react';
import { IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { ICD10_VALUESET, RXNORM_VALUESET } from '../../constants/medical-info';
import { showErrorNotification } from '../../utils/notifications';
import type { ChartFormValues, ComorbidityFormRow, MedicationFormRow, SymptomFormRow } from './encounter-chart-types';

const SNOMED_VALUESET = 'http://hl7.org/fhir/us/core/ValueSet/us-core-condition-code';

const DOSAGE_UNIT_OPTIONS = [
  'mg',
  'mcg',
  'g',
  'mL',
  'L',
  'units',
  'IU',
  'mEq',
  '%',
  'tablet',
  'capsule',
  'patch',
  'drop',
  'spray',
  'puff',
  'suppository',
];

const FREQUENCY_OPTIONS = [
  'Once daily',
  'Twice daily',
  'Three times daily',
  'Four times daily',
  'Every 4 hours',
  'Every 6 hours',
  'Every 8 hours',
  'Every 12 hours',
  'Every 24 hours',
  'Once weekly',
  'Twice weekly',
  'Every other day',
  'Once monthly',
  'Monthly',
  'As needed',
  'At bedtime',
  'In the morning',
  'With meals',
  'Other',
];

const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe', 'Very severe', 'Intermittent', 'Persistent'];

const SYMPTOM_FREQUENCY_OPTIONS = [
  'Nightly',
  'Most nights',
  'Several times a week',
  'Weekly',
  'Occasionally',
  'Rarely',
  'Always',
  'During sleep only',
  'Once daily',
  'Twice daily',
  'Three times daily',
  'Four times daily',
];

const CONDITION_CATEGORY_SYSTEM = 'https://polarissleep.com/fhir/condition-category';

interface PatientConditionSectionProps {
  form: UseFormReturnType<ChartFormValues>;
  patient: Patient;
  encounter: Encounter;
  disabled?: boolean;
  onMedicationsLoaded: (rows: MedicationFormRow[]) => void;
  onSymptomsLoaded: (rows: SymptomFormRow[]) => void;
  onComorbiditiesLoaded: (rows: ComorbidityFormRow[]) => void;
}

export const PatientConditionSection = ({
  form,
  patient,
  encounter,
  disabled = false,
  onMedicationsLoaded,
  onSymptomsLoaded,
  onComorbiditiesLoaded,
}: PatientConditionSectionProps): JSX.Element => {
  const medplum = useMedplum();
  const [medicationSearchKey, setMedicationSearchKey] = useState(0);
  const [symptomSearchKey, setSymptomSearchKey] = useState(0);
  const [comorbiditySearchKey, setComorbiditySearchKey] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: callbacks are stable refs
  useEffect(() => {
    const fetchMedications = async (): Promise<void> => {
      const results = await medplum.searchResources(
        'MedicationStatement',
        `patient=${getReferenceString(patient)}&context=Encounter/${encounter.id}`
      );
      const rows: MedicationFormRow[] = results.map((m) => {
        const dosage = m.dosage?.[0];
        return {
          id: m.id,
          medicationConcept: m.medicationCodeableConcept,
          dosage: dosage?.text ?? '',
          unit: dosage?.doseAndRate?.[0]?.doseQuantity?.unit ?? '',
          frequency: dosage?.timing?.code?.text ?? '',
          dateStarted: (() => {
            const ds = m.effectiveDateTime ?? m.effectivePeriod?.start;
            if (!ds) {
              return null;
            }
            return dayjs(ds).format('YYYY-MM-DD');
          })(),
        };
      });
      onMedicationsLoaded(rows);
    };

    const fetchConditions = async (): Promise<void> => {
      const results = await medplum.searchResources('Condition', `encounter=Encounter/${encounter.id}`);
      const symptoms: SymptomFormRow[] = results
        .filter((c: Condition) =>
          c.category?.some((cat) =>
            cat.coding?.some((cod) => cod.system === CONDITION_CATEGORY_SYSTEM && cod.code === 'symptom')
          )
        )
        .map((c: Condition) => ({
          id: c.id,
          symptom: c.code?.text ?? c.code?.coding?.[0]?.display ?? '',
          severity: c.severity?.text ?? c.severity?.coding?.[0]?.display ?? '',
          frequency: c.note?.[0]?.text ?? '',
          onset: c.onsetDateTime ? dayjs(c.onsetDateTime).format('YYYY-MM-DD') : null,
          snomedConcept: c.code,
        }));
      const comorbidities: ComorbidityFormRow[] = results
        .filter((c: Condition) =>
          c.category?.some((cat) =>
            cat.coding?.some((cod) => cod.system === CONDITION_CATEGORY_SYSTEM && cod.code === 'comorbidity')
          )
        )
        .map((c: Condition) => ({
          id: c.id,
          icd10Concept: c.code,
          onset: c.onsetDateTime ? dayjs(c.onsetDateTime).format('YYYY-MM-DD') : null,
        }));
      onSymptomsLoaded(symptoms);
      onComorbiditiesLoaded(comorbidities);
    };

    fetchMedications().catch(showErrorNotification);
    fetchConditions().catch(showErrorNotification);
  }, [medplum, patient, encounter.id]);

  const medications = form.values.medications;
  const symptoms = form.values.symptoms;
  const comorbidities = form.values.comorbidities;

  return (
    <Card withBorder p={20}>
      <Title order={2} mb={10}>
        Patient Condition
      </Title>
      <Stack gap="lg">
        <Textarea
          label="Chief Complaint(s)"
          placeholder="Describe the primary reason for today's visit..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('chiefComplaint')}
        />

        <Textarea
          label="Assessment"
          placeholder="Clinical assessment and impression..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('assessment')}
        />

        {/* Current Medications */}
        <Stack gap="xs">
          <Text fw={600} size="md">
            Current Medications
          </Text>
          <Box>
            <CodeableConceptInput
              key={medicationSearchKey}
              binding={RXNORM_VALUESET}
              placeholder="Add medication"
              name="medicationSearch"
              path="MedicationStatement.medicationCodeableConcept"
              disabled={disabled}
              onChange={(concept) => {
                if (!concept) {
                  return;
                }
                const row: MedicationFormRow = {
                  id: undefined,
                  medicationConcept: concept,
                  dosage: '',
                  unit: '',
                  frequency: '',
                  dateStarted: null,
                };
                form.insertListItem('medications', row);
                setMedicationSearchKey((k) => k + 1);
              }}
            />
          </Box>
          {medications.map((row, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed" pb={6} style={{ flexShrink: 0, minWidth: 16 }}>
                {index + 1}
              </Text>
              <TextInput
                label={index === 0 ? 'Medication' : undefined}
                placeholder="Medication name"
                disabled={disabled}
                style={{ flex: 2 }}
                value={row.medicationConcept?.text ?? row.medicationConcept?.coding?.[0]?.display ?? ''}
                onChange={(e) =>
                  form.setFieldValue(`medications.${index}.medicationConcept`, {
                    ...row.medicationConcept,
                    text: e.currentTarget.value,
                  })
                }
              />
              <TextInput
                label={index === 0 ? 'Dosage' : undefined}
                placeholder="e.g. 10"
                disabled={disabled}
                style={{ flex: 1 }}
                {...form.getInputProps(`medications.${index}.dosage`)}
              />
              <Autocomplete
                label={index === 0 ? 'Unit' : undefined}
                placeholder="e.g. mg"
                disabled={disabled}
                style={{ flex: 1 }}
                data={DOSAGE_UNIT_OPTIONS}
                {...form.getInputProps(`medications.${index}.unit`)}
              />
              <Autocomplete
                label={index === 0 ? 'Frequency' : undefined}
                placeholder="e.g. Once daily"
                disabled={disabled}
                style={{ flex: 1 }}
                data={FREQUENCY_OPTIONS}
                {...form.getInputProps(`medications.${index}.frequency`)}
              />
              <MonthPickerInput
                label={index === 0 ? 'Date Started' : undefined}
                placeholder="MM/YYYY"
                disabled={disabled}
                style={{ flex: 1 }}
                valueFormat="MM/YYYY"
                maxDate={dayjs().format('YYYY-MM-DD')}
                {...form.getInputProps(`medications.${index}.dateStarted`)}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={disabled}
                style={{ flexShrink: 0 }}
                onClick={() => form.removeListItem('medications', index)}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>

        {/* Symptoms */}
        <Stack gap="xs">
          <Text fw={600} size="md">
            Symptoms
          </Text>
          <Box>
            <CodeableConceptInput
              key={symptomSearchKey}
              binding={SNOMED_VALUESET}
              placeholder="Add symptom"
              name="symptomSearch"
              path="ClinicalImpression.finding.itemCodeableConcept"
              disabled={disabled}
              onChange={(concept) => {
                if (!concept) {
                  return;
                }
                const row: SymptomFormRow = {
                  symptom: concept.text ?? concept.coding?.[0]?.display ?? '',
                  severity: '',
                  frequency: '',
                  onset: null,
                  snomedConcept: concept,
                };
                form.insertListItem('symptoms', row);
                setSymptomSearchKey((k) => k + 1);
              }}
            />
          </Box>
          {symptoms.map((row, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed" pb={6} style={{ flexShrink: 0, minWidth: 16 }}>
                {index + 1}
              </Text>
              <TextInput
                label={index === 0 ? 'Symptom' : undefined}
                placeholder="Symptom description"
                disabled={disabled}
                style={{ flex: 2 }}
                {...form.getInputProps(`symptoms.${index}.symptom`)}
              />
              <Autocomplete
                label={index === 0 ? 'Severity' : undefined}
                placeholder="e.g. Moderate"
                disabled={disabled}
                style={{ flex: 1 }}
                data={SEVERITY_OPTIONS}
                {...form.getInputProps(`symptoms.${index}.severity`)}
              />
              <Autocomplete
                label={index === 0 ? 'Frequency' : undefined}
                placeholder="e.g. Nightly"
                disabled={disabled}
                style={{ flex: 1 }}
                data={SYMPTOM_FREQUENCY_OPTIONS}
                {...form.getInputProps(`symptoms.${index}.frequency`)}
              />
              <MonthPickerInput
                label={index === 0 ? 'Onset' : undefined}
                placeholder="MM/YYYY"
                disabled={disabled}
                style={{ flex: 1 }}
                valueFormat="MM/YYYY"
                maxDate={dayjs().format('YYYY-MM-DD')}
                {...form.getInputProps(`symptoms.${index}.onset`)}
              />
              <TextInput
                label={index === 0 ? 'Code' : undefined}
                disabled
                readOnly
                style={{ flex: 1 }}
                value={row.snomedConcept?.coding?.[0]?.code ?? ''}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={disabled}
                mb={2}
                style={{ flexShrink: 0 }}
                onClick={() => form.removeListItem('symptoms', index)}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>

        {/* Comorbidities */}
        <Stack gap="xs">
          <Text fw={600} size="md">
            Comorbidities
          </Text>
          <Box>
            <CodeableConceptInput
              key={comorbiditySearchKey}
              binding={ICD10_VALUESET}
              placeholder="Add comorbidity"
              name="comorbiditySearch"
              path="Condition.code"
              disabled={disabled}
              onChange={(concept) => {
                if (!concept) {
                  return;
                }
                const row: ComorbidityFormRow = {
                  icd10Concept: concept,
                  onset: null,
                };
                form.insertListItem('comorbidities', row);
                setComorbiditySearchKey((k) => k + 1);
              }}
            />
          </Box>
          {comorbidities.map((row, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed" pb={6} style={{ flexShrink: 0, minWidth: 16 }}>
                {index + 1}
              </Text>
              <TextInput
                label={index === 0 ? 'Condition' : undefined}
                placeholder="Condition name"
                disabled={disabled}
                style={{ flex: 2 }}
                value={row.icd10Concept?.text ?? row.icd10Concept?.coding?.[0]?.display ?? ''}
                onChange={(e) =>
                  form.setFieldValue(`comorbidities.${index}.icd10Concept`, {
                    ...row.icd10Concept,
                    text: e.currentTarget.value,
                  })
                }
              />
              <MonthPickerInput
                label={index === 0 ? 'Onset' : undefined}
                placeholder="MM/YYYY"
                disabled={disabled}
                style={{ flex: 1 }}
                valueFormat="MM/YYYY"
                maxDate={dayjs().format('YYYY-MM-DD')}
                {...form.getInputProps(`comorbidities.${index}.onset`)}
              />
              <TextInput
                label={index === 0 ? 'Code' : undefined}
                disabled
                readOnly
                style={{ flex: 1 }}
                value={row.icd10Concept?.coding?.[0]?.code ?? ''}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={disabled}
                mb={2}
                style={{ flexShrink: 0 }}
                onClick={() => form.removeListItem('comorbidities', index)}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};
